// Signed-out landing with a compact Google sign-in pill under the header icon (owner-approved 2026-11-05).
// Exports and props unchanged (SignIn, SessionCheck, SignInProps); auth flow lives in AuthGate + services/session.
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { C, font } from '../theme/tokens';
import { Photo, Tag } from '../components/ui';
import { Ticker } from '../components/chrome';
import { GoogleMark } from '../components/GoogleMark';
import { fetchAuthMode, legalLinks, type AuthNotice, type ServerAuthMode } from '../services/session';
import { isDemo } from '../lib/clock';

const NOTICE_COPY: Record<AuthNotice, { title: string; body: string; dot: string }> = {
  cancelled: { title: 'Sign-in cancelled.', body: 'Continue whenever you’re ready.', dot: C.violet },
  expired: { title: 'Your session has ended.', body: 'Sign in again to pick up where you left off.', dot: C.amber },
  failed: { title: 'We couldn’t sign you in.', body: 'Please try again in a moment.', dot: C.red },
  suspended: { title: 'This account is paused.', body: 'Contact your UNIT team lead for help.', dot: C.red },
  unconfigured: { title: 'Sign-in isn’t set up in this build.', body: 'Please try again later.', dot: C.amber },
};

export interface SignInProps {
  notice: AuthNotice | null;
  onContinue: () => void;
  forceLoading?: boolean;
}

/** Demo-only review hook: ?signin_open=1 opens the panel on load. */
const previewOpen = (): boolean => isDemo && typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('signin_open') === '1';

function SignInGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
      <path d="M10 16l4-4-4-4" />
      <path d="M14 12H4" />
    </svg>
  );
}

/** UGAIN spelled out; the initials are highlighted inline so the label still reads as continuous text. */
const UGAIN_WORDS = ['Unit', 'Genius', 'Ambassador', 'International', 'Network'];

/** Public landing: UGAIN first. Sign-in lives behind a small header icon. */
export function SignIn({ notice, onContinue, forceLoading }: SignInProps) {
  const [open, setOpen] = useState(() => Boolean(notice) || Boolean(forceLoading) || previewOpen());
  const [loading, setLoading] = useState(false);
  const [legalHint, setLegalHint] = useState<string | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const popId = useId();
  const busy = loading || Boolean(forceLoading);
  const n = notice ? NOTICE_COPY[notice] : null;
  // Which sign-in the server really runs. A local test login is never shown as Google.
  const [mode, setMode] = useState<ServerAuthMode | null>(null);
  useEffect(() => {
    let alive = true;
    void fetchAuthMode().then((m) => alive && setMode(m));
    return () => {
      alive = false;
    };
  }, []);
  const localDev = mode === 'local-dev';

  useEffect(() => {
    if (!open) return;
    panel.current?.querySelector<HTMLElement>('.hs-google')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!busy && panel.current && !panel.current.contains(t) && !trigger.current?.contains(t)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown);
    };
  }, [open, busy]);

  const go = () => {
    if (busy) return;
    setLoading(true);
    onContinue();
  };
  const legal = (label: string, url: string | null) =>
    url ? (
      <a href={url} target="_blank" rel="noopener noreferrer" className="hs-legal">{label}</a>
    ) : (
      <button type="button" className="hs-legal" onClick={() => setLegalHint(`${label} will be linked here before launch.`)}>{label}</button>
    );

  return (
    <div className="hs">
      <header className="hs-top">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <img src={`${import.meta.env.BASE_URL}brand/unit-mark-violet.svg`} alt="UNIT" style={{ height: 22, width: 'auto' }} />
          <span className="hs-label">
            {UGAIN_WORDS.map((w, i) => (
              <Fragment key={w}>
                {i > 0 && ' '}
                <span className="hs-label-i">{w[0]}</span>
                {w.slice(1)}
              </Fragment>
            ))}
          </span>
        </div>
        <div className="hs-actions">
          {isDemo && <Tag label="DEMO" bg={C.amberBg} fg={C.amber} />}
          {localDev && <span title="Local prototype: Google sign-in is not connected"><Tag label="LOCAL" bg={C.amberBg} fg={C.amber} /></span>}
          <span className="hs-trigger-wrap">
            <button ref={trigger} type="button" className="hs-trigger press" aria-label="Sign in to UGAIN" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? popId : undefined} onClick={() => setOpen((o) => !o)}>
              <SignInGlyph />
              {n && !open && <span className="hs-badge" style={{ background: n.dot }} aria-hidden="true" />}
            </button>
            <span className="hs-tip" role="tooltip">Sign in</span>
            {open && (
              <div ref={panel} id={popId} role="dialog" aria-label="Sign in to UGAIN" className="hs-pop">
                <button type="button" onClick={go} disabled={busy} aria-busy={busy} className="hs-google press">
                  {busy ? <span className="spin" aria-hidden="true" style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${C.subtle}`, borderTopColor: C.violet, flex: 'none' }} /> : localDev ? null : <GoogleMark size={18} />}
                  <span>{localDev ? (busy ? 'Opening…' : 'Continue with local test login') : busy ? 'Opening Google…' : notice === 'suspended' ? 'Use a different Google account' : 'Sign in with Google'}</span>
                </button>
                {n && (
                  <div role={notice === 'cancelled' ? 'status' : 'alert'} className="hs-notice">
                    <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: '50%', background: n.dot, flex: 'none', marginTop: 6 }} />
                    <span><b style={{ fontWeight: 600 }}>{n.title}</b> {n.body}</span>
                  </div>
                )}
                {isDemo && <span role="note" className="hs-pop-note">Demo mode: this button does not contact Google.</span>}
                {localDev && <span role="note" className="hs-pop-note" style={{ fontWeight: 700, letterSpacing: '.02em' }}>LOCAL PROTOTYPE — GOOGLE NOT CONNECTED. Opens a localhost-only test login.</span>}
              </div>
            )}
          </span>
        </div>
      </header>

      <main className="hs-main">
        <h1 className="hs-mast" aria-label="UGAIN">UGAIN</h1>
        <p className="hs-tag">Stay in the pulse of UNIT. Ambassadors worldwide, organising, creating and sharing what happens next.</p>
        <div className="hs-collage" aria-hidden="true">
          <div className="hs-ph hs-ph--a"><Photo id="london-hunt" eager alt="" sizes="(max-width: 899px) 64vw, 40vw" /></div>
          <div className="hs-ph hs-ph--b"><Photo id="concert-crowd" eager alt="" sizes="(max-width: 899px) 42vw, 24vw" /></div>
          <div className="hs-ph hs-ph--c"><Photo id="campus-walk" eager alt="" sizes="(max-width: 899px) 32vw, 18vw" /></div>
          <div className="hs-sticker float">U GAIN.<br />WE ALL<br />GAIN.</div>
        </div>
        <div className="hs-ticker"><Ticker items={['U GAIN. WE ALL GAIN.', 'STAY IN THE PULSE', 'BE PART OF THE ACTION']} /></div>
      </main>

      <footer className="hs-foot">
        <nav aria-label="Legal" className="hs-legalrow">
          {legal('Privacy Policy', legalLinks.privacy)}
          <span aria-hidden="true">·</span>
          {legal('Terms of Use', legalLinks.terms)}
        </nav>
        {legalHint && <span role="status" style={{ font: font(400, 11, 1.4), color: C.grey }}>{legalHint}</span>}
      </footer>

    </div>
  );
}

/** Returning-session check (unchanged). */
export function SessionCheck() {
  return (
    <div className="signin-check" role="status" aria-live="polite">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }} className="fade">
        <img src={`${import.meta.env.BASE_URL}brand/unit-mark-violet.svg`} alt="UNIT" style={{ height: 30, width: 'auto' }} />
        <span style={{ fontFamily: 'Geist, system-ui, sans-serif', fontWeight: 800, fontSize: 'min(88px, 24vw)', lineHeight: 0.84, letterSpacing: '-.07em', color: C.white }}>UGAIN</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, font: font(600, 10), letterSpacing: '.22em', color: C.mutedDark }}>
          <span className="live-dot" style={{ width: 7, height: 7, borderRadius: '50%', background: C.mark }} />CHECKING YOUR SESSION
        </span>
      </div>
    </div>
  );
}
